import { LaunchOutlined as LaunchIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { appUrl } from '@/utils/appUrl';

export interface BulkFailureItem {
  shippingNumber: string;
  reason: string;
  isLinkable: boolean;
}

interface BulkUploadFailuresByReasonProps {
  failures: BulkFailureItem[];
}

const BulkUploadFailuresByReason = ({ failures }: BulkUploadFailuresByReasonProps) => {
  const { showNotification } = useNotification();

  const groups = new Map<string, BulkFailureItem[]>();
  for (const failure of failures) {
    const items = groups.get(failure.reason) ?? [];
    items.push(failure);
    groups.set(failure.reason, items);
  }

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      showNotification('Código copiado al portapapeles', NotificationSeverity.success);
    } catch {
      showNotification('No se pudo copiar el código', NotificationSeverity.error);
    }
  };

  return (
    <div className='flex flex-col gap-3 mt-2 max-h-[340px] overflow-y-auto pr-1'>
      {Array.from(groups.entries()).map(([reason, items]) => (
        <div
          key={reason}
          className='flex flex-col gap-2 p-3 rounded-lg bg-red-50 border border-red-100'
        >
          <span className='text-red-900 font-bold text-[14px] leading-relaxed'>
            {reason} <span className='font-medium text-red-700'>({items.length})</span>
          </span>
          <div className='flex flex-wrap gap-x-3 gap-y-1'>
            {items.map((item) =>
              item.isLinkable ? (
                <a
                  key={item.shippingNumber}
                  href={appUrl(`/business/shipping/${item.shippingNumber}`)}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-black text-[#2196f3] hover:text-[#1976d2] hover:underline text-[14px] flex items-center gap-1 transition-colors'
                  title='Ver detalle del envío'
                >
                  <LaunchIcon sx={{ fontSize: 15 }} />
                  {item.shippingNumber}
                </a>
              ) : (
                <button
                  key={item.shippingNumber}
                  type='button'
                  onClick={() => handleCopy(item.shippingNumber)}
                  className='font-black text-red-800 text-[14px] flex items-center gap-1 hover:text-red-900 transition-colors cursor-pointer'
                  title='No se pudo resolver a un pedido interno — copiar código para buscarlo manualmente'
                >
                  <CopyIcon sx={{ fontSize: 15 }} />
                  {item.shippingNumber}
                </button>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BulkUploadFailuresByReason;
